import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Download, Clock, CheckCircle, AlertCircle, 
  Plus, Sparkles, FileArchive, Shield, ArrowRight,
  Building2, Plane, Heart, Factory, Pill
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import ErrorDisplay from '@/components/ErrorDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRY_ICONS = {
  'Manufacturing': Factory,
  'Defense': Shield,
  'Aerospace': Plane,
  'Healthcare': Heart,
  'Pharmaceutical': Pill,
  'Construction': Building2
};

const TIER_COLORS = {
  'pilot': 'bg-gray-100 text-gray-700',
  'pro': 'bg-blue-100 text-blue-700',
  'enterprise': 'bg-purple-100 text-purple-700',
  'defense': 'bg-red-100 text-red-700'
};

export default function AuditBundles({ user, logout }) {
  const navigate = useNavigate();
  const [bundles, setBundles] = useState([]);
  const [customBundles, setCustomBundles] = useState([]);
  const [recentExports, setRecentExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(null);

  useEffect(() => {
    fetchBundles();
  }, []);

  const fetchBundles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('standari_token');
      const response = await axios.get(`${BACKEND_URL}/api/audit-bundles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBundles(response.data.available_bundles || []);
      setCustomBundles(response.data.custom_bundles || []);
      setRecentExports(response.data.recent_exports || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBundle = async (bundleId) => {
    try {
      setGenerating(bundleId);
      setError(null);
      
      const token = localStorage.getItem('standari_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/audit-bundles/generate`,
        {
          bundle_type: bundleId,
          include_signatures: true,
          format_preference: 'zip'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh recent exports
      await fetchBundles();
      
      // Auto-download if completed
      if (response.data.status === 'COMPLETED') {
        window.location.href = `${BACKEND_URL}/api/audit-bundles/download/${response.data.id}`;
      }
      
    } catch (err) {
      setError(err);
    } finally {
      setGenerating(null);
    }
  };

  const handleDownload = (bundleId) => {
    const token = localStorage.getItem('standari_token');
    window.location.href = `${BACKEND_URL}/api/audit-bundles/download/${bundleId}?token=${token}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading audit bundles...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <FileArchive className="h-8 w-8 mr-3 text-blue-600" />
                Audit Bundles
              </h1>
              <p className="text-gray-600 mt-1">
                One-click compliance packages ready for any inspection
              </p>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6">
            <ErrorDisplay error={error} onRetry={fetchBundles} />
          </div>
        )}

        {/* Hero Banner */}
        <Card className="mb-8 bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0">
          <CardContent className="pt-8 pb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center mb-3">
                  <Sparkles className="h-6 w-6 mr-2" />
                  <h2 className="text-2xl font-bold">Ready for Your Next Audit?</h2>
                </div>
                <p className="text-blue-100 text-lg mb-4">
                  Generate complete, audit-ready documentation packages in seconds.
                  No more scrambling when the inspector arrives.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-white/20 text-white border-0">✓ All Required Documents</Badge>
                  <Badge className="bg-white/20 text-white border-0">✓ Compliance Reports</Badge>
                  <Badge className="bg-white/20 text-white border-0">✓ Audit Trails</Badge>
                  <Badge className="bg-white/20 text-white border-0">✓ Standards History</Badge>
                </div>
              </div>
              <div className="flex-shrink-0">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                  <Package className="h-16 w-16 mx-auto mb-2" />
                  <p className="text-sm font-semibold">{bundles.length} Bundles Available</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Available Bundles */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Available Bundles</h2>
            {user?.tier === 'enterprise' || user?.tier === 'defense' ? (
              <Button onClick={() => navigate('/audit-bundles/custom')} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Create Custom Bundle
              </Button>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bundles.map((bundle) => {
              const IndustryIcon = INDUSTRY_ICONS[bundle.industry?.split(',')[0]?.trim()] || Building2;
              const isGenerating = generating === bundle.id;
              
              return (
                <Card 
                  key={bundle.id} 
                  className="hover:shadow-lg transition-shadow duration-200 border-2 hover:border-blue-300"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <IndustryIcon className="h-8 w-8 text-blue-600" />
                      <Badge className={TIER_COLORS[bundle.tier_required]}>
                        {bundle.tier_required}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{bundle.name}</CardTitle>
                    <CardDescription className="text-sm line-clamp-2">
                      {bundle.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* Quick Stats */}
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center text-gray-600">
                          <Package className="h-4 w-4 mr-1" />
                          ~{bundle.estimated_size_mb}MB
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Shield className="h-4 w-4 mr-1" />
                          {bundle.standards.length} Standards
                        </div>
                      </div>

                      {/* Use Case */}
                      <div className="bg-gray-50 rounded p-2">
                        <p className="text-xs text-gray-600">
                          <span className="font-semibold">Typical Use:</span> {bundle.typical_use_case}
                        </p>
                      </div>

                      {/* Action Button */}
                      <Button 
                        onClick={() => handleGenerateBundle(bundle.id)}
                        disabled={isGenerating}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        {isGenerating ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Generate Bundle
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {bundles.length === 0 && (
            <Card className="text-center py-12">
              <CardContent>
                <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-2">No bundles available for your tier</p>
                <p className="text-gray-500 text-sm mb-4">
                  Upgrade to access pre-configured audit bundles
                </p>
                <Button onClick={() => navigate('/upgrade')} className="bg-purple-600 hover:bg-purple-700">
                  View Upgrade Options
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Recent Exports */}
        {recentExports.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Recent Exports</h2>
            <div className="space-y-3">
              {recentExports.slice(0, 5).map((exportItem) => (
                <Card key={exportItem.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className={`p-3 rounded-lg ${
                          exportItem.status === 'COMPLETED' ? 'bg-green-100' :
                          exportItem.status === 'GENERATING' ? 'bg-blue-100' :
                          exportItem.status === 'FAILED' ? 'bg-red-100' :
                          'bg-gray-100'
                        }`}>
                          {exportItem.status === 'COMPLETED' ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : exportItem.status === 'GENERATING' ? (
                            <Clock className="h-5 w-5 text-blue-600" />
                          ) : (
                            <AlertCircle className="h-5 w-5 text-red-600" />
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{exportItem.bundle_name}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(exportItem.created_at).toLocaleString()}
                            {exportItem.file_size_bytes && (
                              <> · {(exportItem.file_size_bytes / 1024 / 1024).toFixed(1)}MB</>
                            )}
                          </p>
                        </div>

                        <Badge className={TIER_COLORS[exportItem.status?.toLowerCase() || 'pilot']}>
                          {exportItem.status}
                        </Badge>
                      </div>

                      {exportItem.status === 'COMPLETED' && (
                        <Button 
                          onClick={() => handleDownload(exportItem.id)}
                          size="sm"
                          className="ml-4"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {recentExports.length === 0 && bundles.length > 0 && (
          <Card className="text-center py-12 bg-gray-50 border-dashed">
            <CardContent>
              <FileArchive className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg mb-2">No exports yet</p>
              <p className="text-gray-500 text-sm">
                Generate your first audit bundle by clicking any bundle above
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
